import { useEffect, useState } from 'react';
import { ArrowSquareOut } from 'phosphor-react';
import { createPortal } from 'react-dom';
import { buildPublicUrl, formatBytes } from '../../lib/libraryUtils';
import { TYPE_LABELS } from '../../lib/libraryConfig';

export const AssetPreviewModal = ({ asset, onClose }) => {
  const [mediaFailed, setMediaFailed] = useState(false);

  useEffect(() => {
    setMediaFailed(false);
  }, [asset?.id]);

  useEffect(() => {
    if (!asset) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [asset, onClose]);

  if (!asset) return null;

  const publicUrl = buildPublicUrl(asset.path);
  const title = asset.displayName || asset.name;
  const subtitle = asset.sourceLabel ? `${asset.categoryLabel} · ${asset.sourceLabel}` : asset.categoryLabel;
  const isImageOrVideo = asset.type === 'image' || asset.type === 'video';

  const modal = (
    <div className="modal-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className="modal-content glass-panel relative z-[2100]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-black text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="chip">
            إغلاق
          </button>
        </div>

        <div className="mt-5">
          {asset.type === 'image' && !mediaFailed && (
            <img
              src={publicUrl}
              alt={title}
              className="w-full max-h-[60vh] object-contain rounded-2xl bg-white/70"
              onError={() => setMediaFailed(true)}
            />
          )}

          {asset.type === 'video' && !mediaFailed && (
            <video
              src={publicUrl}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              controls
              className="w-full max-h-[60vh] rounded-2xl bg-black"
              onError={() => setMediaFailed(true)}
            />
          )}

          {asset.type === 'audio' && (
            <div className="rounded-2xl border border-white/40 bg-white/60 p-4">
              <audio src={publicUrl} controls className="w-full" />
            </div>
          )}

          {asset.type === 'document' && asset.ext === 'pdf' && (
            <iframe
              title={title}
              src={publicUrl}
              className="w-full h-[60vh] rounded-2xl border border-white/40 bg-white"
            />
          )}

          {mediaFailed && isImageOrVideo && (
            <div className="rounded-2xl border border-amber-300/70 bg-amber-50 p-4 text-amber-900">
              <p className="font-semibold">تعذر تحميل المعاينة داخل الصفحة.</p>
              <p className="text-sm mt-1">يمكنك فتح الملف في تبويب جديد من الزر بالأسفل.</p>
            </div>
          )}

          {(asset.type === 'other' || (asset.type === 'document' && asset.ext !== 'pdf')) && (
            <div className="rounded-2xl border border-white/40 bg-white/60 p-6 text-center">
              <p className="font-semibold text-gray-900">لا توجد معاينة مباشرة لهذا النوع.</p>
              <p className="text-sm text-gray-600 mt-1">استخدم زر الفتح الخارجي لعرض الملف.</p>
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-white/40 bg-white/60 p-4 space-y-2">
            <p className="font-black text-gray-900">معلومات سريعة</p>
            <p className="text-sm text-gray-700">النوع: {TYPE_LABELS[asset.type] || asset.type}</p>
            <p className="text-sm text-gray-700">الحجم: {formatBytes(asset.size)}</p>
          </div>

          <div className="rounded-2xl border border-white/40 bg-white/60 p-4 space-y-2">
            <p className="font-black text-gray-900">الوصف التعليمي</p>
            <p className="text-sm text-gray-700">{asset.description}</p>
            <p className="text-sm text-sky-700 font-semibold">{asset.usageHint}</p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <a href={publicUrl} target="_blank" rel="noreferrer" className="chip chip-strong">
            <ArrowSquareOut size={16} weight="bold" />
            فتح في تبويب جديد
          </a>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.documentElement);
};
